import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AdminService, Booking } from '@app/core/services/admin.service';
import { ToastService } from '@app/core/services/toast.service';
import { MessageChatComponent } from '@app/shared/components/message-chat.component';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-booking-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, MessageChatComponent],
  templateUrl: './booking-edit.component.html',
  styleUrls: ['./booking-edit.component.scss']
})
export class BookingEditComponent implements OnInit, OnDestroy {
  @ViewChild('messageChatComponent') messageChatComponent?: MessageChatComponent;
  editingBooking: Booking | null = null;
  originalBookingId: string | null = null; // Speichere die originale DB-ID
  messageUnreadCount: number = 0;
  isSaving = false;
  isCreatingOrderConfirmation = false;
  private subscription = new Subscription();

  constructor(
    private adminService: AdminService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.route.params.subscribe(params => {
        const bookingId = params['id'];
        if (bookingId) {
          this.loadBooking(bookingId);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onUnreadCountChange(count: number): void {
    this.messageUnreadCount = count;
    this.cdr.detectChanges();
  }

  scrollToMessages(): void {
    if (this.messageChatComponent) {
      this.messageChatComponent.scrollToMessages();
    }
  }

  loadBooking(id: string) {
    console.log('[BookingEdit] Loading booking with ID:', id);
    this.adminService.getAllBookings().subscribe({
      next: (bookings) => {
        console.log('[BookingEdit] All bookings:', bookings);
        console.log('[BookingEdit] Searching for ID:', id);
        
        // Versuche die Buchung zu finden - entweder direkt über ID oder über formatierte ID
        const booking = bookings.find(b => {
          const directMatch = b.id === id;
          const formattedMatch = this.formatBookingId(b.id) === id;
          console.log(`[BookingEdit] Checking booking ${b.id}: direct=${directMatch}, formatted=${formattedMatch}`);
          return directMatch || formattedMatch;
        });
        
        console.log('[BookingEdit] Found booking:', booking);
        
        if (booking) {
          this.originalBookingId = booking.id || null; // Speichere die originale ID
          this.editingBooking = { ...booking };
          console.log('[BookingEdit] Before normalize - status:', this.editingBooking.status, 'Type:', typeof this.editingBooking.status);
          this.normalizeStatus();
          console.log('[BookingEdit] After normalize - status:', this.editingBooking.status);
          this.cdr.detectChanges();
          console.log('[BookingEdit] editingBooking set:', this.editingBooking);
          console.log('[BookingEdit] originalBookingId:', this.originalBookingId);
        } else {
          console.error('[BookingEdit] Booking not found for ID:', id);
          this.toastService.error('Buchung nicht gefunden');
          this.navigateBack();
        }
      },
      error: (err) => {
        console.error('[BookingEdit] Error loading bookings:', err);
        this.toastService.error('Fehler beim Laden der Buchung');
        this.navigateBack();
      }
    });
  }

  normalizeStatus() {
    if (!this.editingBooking) return;
    
    console.log('[BookingEdit] normalizeStatus - Original status:', this.editingBooking.status, 'Type:', typeof this.editingBooking.status);
    
    // Wenn Status null oder undefined ist, setze auf 'New'
    if (this.editingBooking.status === null || this.editingBooking.status === undefined) {
      console.log('[BookingEdit] normalizeStatus - Status is null/undefined, setting to New');
      this.editingBooking.status = 'New' as any;
      return;
    }
    
    // Konvertiere zu String (OHNE || '' weil 0 sonst zu leerem String wird!)
    let statusStr = String(this.editingBooking.status).trim();
    console.log('[BookingEdit] normalizeStatus - Converted to string:', statusStr, 'Type:', typeof statusStr);
    
    // Entferne mögliche Zahlen-Mappings falls Backend Enums als Zahlen sendet
    const statusMap: any = {
      '0': 'New',
      '1': 'Confirmed', 
      '2': 'Cancelled'
    };
    
    console.log('[BookingEdit] normalizeStatus - Checking statusMap[' + statusStr + ']:', statusMap[statusStr]);
    if (statusMap[statusStr]) {
      this.editingBooking.status = statusMap[statusStr] as any;
      console.log('[BookingEdit] normalizeStatus - Mapped from number:', this.editingBooking.status);
      return;
    }
    console.log('[BookingEdit] normalizeStatus - No match in statusMap');
    
    // Wenn es bereits einer der korrekten Werte ist, behalte ihn
    if (statusStr === 'New' || statusStr === 'Confirmed' || statusStr === 'Cancelled') {
      this.editingBooking.status = statusStr as any;
      console.log('[BookingEdit] normalizeStatus - Already correct:', this.editingBooking.status);
      return;
    }
    
    // Andernfalls: Case-insensitive matching
    const lowerStatus = statusStr.toLowerCase();
    if (lowerStatus === 'new' || lowerStatus === 'pending') {
      this.editingBooking.status = 'New' as any;
    } else if (lowerStatus === 'confirmed') {
      this.editingBooking.status = 'Confirmed' as any;
    } else if (lowerStatus === 'cancelled' || lowerStatus === 'canceled') {
      this.editingBooking.status = 'Cancelled' as any;
    }
    console.log('[BookingEdit] normalizeStatus - Final status:', this.editingBooking.status);
  }

  saveBooking() {
    if (!this.editingBooking || !this.originalBookingId) return;

    this.isSaving = true;
    this.adminService.updateBookingStatus(
      this.originalBookingId,
      this.editingBooking.status,
      this.editingBooking.notes
    ).subscribe({
      next: () => {
        this.isSaving = false;
        this.toastService.success('Buchung aktualisiert');
        this.navigateBack();
      },
      error: () => {
        this.isSaving = false;
        this.toastService.error('Fehler beim Aktualisieren');
      }
    });
  }

  updateStatus(status: string) {
    if (!this.editingBooking || !this.originalBookingId) return;

    this.isSaving = true;
    this.adminService.updateBookingStatus(
      this.originalBookingId,
      status,
      this.editingBooking.notes
    ).subscribe({
      next: () => {
        this.isSaving = false;
        this.editingBooking!.status = status as any;
        this.toastService.success('Status aktualisiert');
      },
      error: () => {
        this.isSaving = false;
        this.toastService.error('Fehler beim Aktualisieren');
      }
    });
  }

  navigateBack() {
    this.router.navigate(['/admin']);
  }

  openCustomerView() {
    if (!this.originalBookingId || !this.editingBooking?.magicToken) {
      this.toastService.error('Kein Magic Token verfügbar');
      return;
    }
    
    const url = `/booking/${this.originalBookingId}?token=${this.editingBooking.magicToken}`;
    window.open(url, '_blank');
  }

  getStatusString(status: any): string {
    if (typeof status === 'string') {
      return status;
    }
    return String(status || '');
  }

  getStatusColor(status: any): string {
    const statusStr = this.getStatusString(status).toLowerCase();
    switch (statusStr) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  formatBookingId(id?: string): string {
    if (!id) return '';
    if (id.startsWith('VW-')) return id;
    
    const year = new Date().getFullYear();
    
    // Handle DB Format: "bookings/97-A" or "97-A"
    let cleanId = id.replace('bookings/', '');
    cleanId = cleanId.replace('-A', ''); // Remove suffix like -A, -B etc
    
    // Pad number with zeros to 3 digits
    const number = cleanId.trim().padStart(3, '0');
    return `VW-${year}-${number}`;
  }

  createOrUpdateOrderConfirmation() {
    if (!this.originalBookingId) {
      this.toastService.error('Keine Buchung ausgewählt');
      return;
    }

    this.isCreatingOrderConfirmation = true;
    const url = `${environment.apiUrl}/api/bookings/${this.originalBookingId}/order-confirmation/sevdesk/create-or-update`;
    
    // Der AuthInterceptor fügt automatisch das Token hinzu
    this.http.post<any>(url, {}).subscribe({
      next: (response) => {
        this.isCreatingOrderConfirmation = false;
        this.toastService.success(response.message || 'Auftragsbestätigung wurde erstellt/aktualisiert');
        
        // Aktualisiere die Booking-Daten mit der Sevdesk Order ID
        if (this.editingBooking) {
          this.editingBooking.sevdeskOrderId = response.sevdeskOrderId;
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isCreatingOrderConfirmation = false;
        this.toastService.error('Fehler beim Erstellen der Auftragsbestätigung');
        console.error('Error creating order confirmation:', err);
      }
    });
  }

  deleteOrderConfirmation() {
    if (!this.originalBookingId) {
      this.toastService.error('Keine Buchung ausgewählt');
      return;
    }

    if (!this.editingBooking?.sevdeskOrderId) {
      this.toastService.error('Diese Buchung hat keine Sevdesk-Auftragsbestätigung');
      return;
    }

    if (!confirm('Möchten Sie die Auftragsbestätigung wirklich löschen? Dies kann nicht rückgängig gemacht werden.')) {
      return;
    }

    this.isCreatingOrderConfirmation = true;
    const url = `${environment.apiUrl}/api/bookings/${this.originalBookingId}/order-confirmation/sevdesk`;
    
    this.http.delete<any>(url).subscribe({
      next: (response) => {
        this.isCreatingOrderConfirmation = false;
        this.toastService.success(response.message || 'Auftragsbestätigung wurde gelöscht');
        
        // Aktualisiere die Booking-Daten
        if (this.editingBooking) {
          this.editingBooking.sevdeskOrderId = undefined;
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isCreatingOrderConfirmation = false;
        this.toastService.error('Fehler beim Löschen der Auftragsbestätigung');
        console.error('Error deleting order confirmation:', err);
      }
    });
  }
}
